import type React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import LoginPage from './auth/login-page';
import ProtectedRoute from './auth/protected-route';
import ProLayout from './layouts/pro-layout';
import { OLD_REDIRECTS } from './layouts/menu-config';

// 现有 11 页面
const Overview = lazy(() => import('./pages/overview'));
const StoresList = lazy(() => import('./pages/stores/list'));
const StoresDetail = lazy(() => import('./pages/stores/detail'));
const StoreManagersList = lazy(() => import('./pages/stores/managers'));
const AccountsList = lazy(() => import('./pages/accounts/list'));
const AccountsDetail = lazy(() => import('./pages/accounts/detail'));
const PackagesList = lazy(() => import('./pages/packages/list'));
const OrdersList = lazy(() => import('./pages/orders/list'));
const MessagesBroadcast = lazy(() => import('./pages/messages/broadcast'));
const AuditLogsList = lazy(() => import('./pages/audit-logs/list'));
const RescheduleList = lazy(() => import('./pages/reschedule-requests/list'));
const CouponsIssue = lazy(() => import('./pages/coupons/issue'));
const CourseTemplatesList = lazy(() => import('./pages/courses/templates/list'));
const CourseTemplateDetail = lazy(() => import('./pages/courses/templates/detail'));
const CourseSessionsList = lazy(() => import('./pages/courses/sessions/list'));
const CourseSessionNew = lazy(() => import('./pages/courses/sessions/new'));
const CourseSessionBulkCopy = lazy(() => import('./pages/courses/sessions/bulk-copy'));
const BookingsList = lazy(() => import('./pages/courses/bookings/list'));
const BookingLedger = lazy(() => import('./pages/courses/booking-ledger/list'));
const StudentsList = lazy(() => import('./pages/members/students/list'));
const StudentDetail = lazy(() => import('./pages/members/students/detail'));
const CoachesList = lazy(() => import('./pages/courses/coaches/list'));
const CoachDetail = lazy(() => import('./pages/courses/coaches/detail'));
const TherapistsList = lazy(() => import('./pages/rehabs/therapists/list'));
const TherapistDetail = lazy(() => import('./pages/rehabs/therapists/detail'));
const AppointmentsList = lazy(() => import('./pages/rehabs/appointments/list'));
const RecordsList = lazy(() => import('./pages/rehabs/records/list'));
const RehabPackagesList = lazy(() => import('./pages/rehabs/packages/list'));
const RehabSchedulingList = lazy(() => import('./pages/rehabs/scheduling/list'));
const RehabSchedulingBulkCopy = lazy(() => import('./pages/rehabs/scheduling/bulk-copy'));
const RevenueDashboard = lazy(() => import('./pages/dashboard/revenue'));
const ClassConsumptionDashboard = lazy(() => import('./pages/dashboard/class-consumption'));
const RehabConsumptionDashboard = lazy(() => import('./pages/dashboard/rehab-consumption'));
const RetentionDashboard = lazy(() => import('./pages/dashboard/retention'));
const RechargeDetailDashboard = lazy(() => import('./pages/dashboard/recharge-detail'));
const ExchangeDetailTrainingDashboard = lazy(() => import('./pages/dashboard/exchange-detail'));
const ExchangeDetailRehabDashboard = lazy(() => import('./pages/dashboard/exchange-detail-rehab'));
const BookingDetailTrainingDashboard = lazy(() => import('./pages/dashboard/booking-detail'));
const BookingDetailRehabDashboard = lazy(() => import('./pages/dashboard/booking-detail-rehab'));
const BannersList = lazy(() => import('./pages/ops/banners/list'));
const TagsList = lazy(() => import('./pages/ops/tags/list'));
const OfflineOrderNew = lazy(() => import('./pages/ops/offline-orders/new'));
const ReportsBySource = lazy(() => import('./pages/reports/by-source'));
const BookingGrid = lazy(() => import('./pages/dashboard/booking-grid'));
const BusinessStaffList = lazy(() => import('./pages/stores/business'));
const PendingConfirmList = lazy(() => import('./pages/ops/offline-orders/pending'));
const CouponsList = lazy(() => import('./pages/ops/coupons/list'));
const CouponUsageList = lazy(() => import('./pages/ops/coupons/usage'));
const PointsAccountsList = lazy(() => import('./pages/ops/points/list'));
const RewardItemsList = lazy(() => import('./pages/ops/reward-items/list'));
const ExchangesList = lazy(() => import('./pages/ops/exchanges/list'));
const MarketingInviteSettings = lazy(() => import('./pages/marketing/invites/settings'));
const MarketingInviteList = lazy(() => import('./pages/marketing/invites/list'));
const MarketingInviteDraws = lazy(() => import('./pages/marketing/invites/draws'));
const RechargeManagePage = lazy(() => import('./pages/ops/recharge/index'));
const RechargeOrdersList = lazy(() => import('./pages/ops/recharge-orders/list'));
const StaffList = lazy(() => import('./pages/system/staff/list'));
const AdminsList = lazy(() => import('./pages/system/admins/list'));
const RolesPage = lazy(() => import('./pages/system/roles'));
const MyProfile = lazy(() => import('./pages/system/me'));
const SystemAbout = lazy(() => import('./pages/system/about'));
const SystemAgreements = lazy(() => import('./pages/system/agreements'));
const TrainingRecordsList = lazy(() => import('./pages/courses/training-records/list'));

const Placeholder = lazy(() => import('./pages/placeholder'));

function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback="加载中...">{children}</Suspense>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute><ProLayout /></ProtectedRoute>}>
        {Object.entries(OLD_REDIRECTS).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}

        <Route path="/dashboard/overview" element={<Page><Overview /></Page>} />
        <Route path="/dashboard/revenue" element={<Page><RevenueDashboard /></Page>} />
        <Route path="/dashboard/class-consumption" element={<Page><ClassConsumptionDashboard /></Page>} />
        <Route path="/dashboard/rehab-consumption" element={<Page><RehabConsumptionDashboard /></Page>} />
        <Route path="/dashboard/retention" element={<Page><RetentionDashboard /></Page>} />
        <Route path="/dashboard/recharge-detail" element={<Page><RechargeDetailDashboard /></Page>} />
        <Route path="/dashboard/exchange-detail-training" element={<Page><ExchangeDetailTrainingDashboard /></Page>} />
        <Route path="/dashboard/exchange-detail-rehab" element={<Page><ExchangeDetailRehabDashboard /></Page>} />
        <Route path="/dashboard/booking-detail-training" element={<Page><BookingDetailTrainingDashboard /></Page>} />
        <Route path="/dashboard/booking-detail-rehab" element={<Page><BookingDetailRehabDashboard /></Page>} />

        <Route path="/members/accounts" element={<Page><AccountsList /></Page>} />
        <Route path="/members/accounts/:id" element={<Page><AccountsDetail /></Page>} />
        <Route path="/members/students" element={<Page><StudentsList /></Page>} />
        <Route path="/members/students/:id" element={<Page><StudentDetail /></Page>} />

        <Route path="/courses/templates" element={<Page><CourseTemplatesList /></Page>} />
        <Route path="/courses/templates/:id" element={<Page><CourseTemplateDetail /></Page>} />
        <Route path="/courses/packages" element={<Page><PackagesList /></Page>} />
        <Route path="/courses/sessions" element={<Page><CourseSessionsList /></Page>} />
        <Route path="/courses/sessions/new" element={<Page><CourseSessionNew /></Page>} />
        <Route path="/courses/sessions/bulk-copy" element={<Page><CourseSessionBulkCopy /></Page>} />
        <Route path="/courses/coaches" element={<Page><CoachesList /></Page>} />
        <Route path="/courses/coaches/:id" element={<Page><CoachDetail /></Page>} />
        <Route path="/courses/bookings" element={<Page><BookingsList /></Page>} />
        <Route path="/courses/booking-ledger" element={<Page><BookingLedger /></Page>} />
        <Route path="/courses/training-records" element={<Page><TrainingRecordsList /></Page>} />
        <Route path="/courses/reschedule-requests" element={<Page><RescheduleList /></Page>} />

        <Route path="/rehabs/therapists" element={<Page><TherapistsList /></Page>} />
        <Route path="/rehabs/therapists/:id" element={<Page><TherapistDetail /></Page>} />
        <Route path="/rehabs/appointments" element={<Page><AppointmentsList /></Page>} />
        <Route path="/rehabs/records" element={<Page><RecordsList /></Page>} />
        <Route path="/rehabs/packages" element={<Page><RehabPackagesList /></Page>} />
        <Route path="/rehabs/scheduling" element={<Page><RehabSchedulingList /></Page>} />
        <Route path="/rehabs/scheduling/bulk-copy" element={<Page><RehabSchedulingBulkCopy /></Page>} />

        <Route path="/ops/banners" element={<Page><BannersList /></Page>} />
        <Route path="/ops/tags" element={<Page><TagsList /></Page>} />
        <Route path="/ops/coupons" element={<Page><CouponsList /></Page>} />
        <Route path="/ops/coupons/issue" element={<Page><CouponsIssue /></Page>} />
        <Route path="/ops/coupons/usage" element={<Page><CouponUsageList /></Page>} />
        <Route path="/ops/points" element={<Page><PointsAccountsList /></Page>} />
        <Route path="/ops/reward-items" element={<Page><RewardItemsList /></Page>} />
        <Route path="/ops/exchanges" element={<Page><ExchangesList /></Page>} />
        <Route path="/ops/orders" element={<Page><OrdersList /></Page>} />
        <Route path="/ops/offline-orders/new" element={<Page><OfflineOrderNew /></Page>} />
        <Route path="/reports/by-source" element={<Page><ReportsBySource /></Page>} />
        <Route path="/dashboard/booking-grid-training" element={<Page><BookingGrid type="training" /></Page>} />
        <Route path="/dashboard/booking-grid-rehab" element={<Page><BookingGrid type="rehab" /></Page>} />
        <Route path="/stores/business" element={<Page><BusinessStaffList /></Page>} />
        <Route path="/ops/offline-orders/pending" element={<Page><PendingConfirmList /></Page>} />
        <Route path="/ops/recharge" element={<Page><RechargeManagePage /></Page>} />
        <Route path="/ops/recharge-orders" element={<Page><RechargeOrdersList /></Page>} />
        <Route path="/ops/messages" element={<Page><MessagesBroadcast /></Page>} />

        <Route path="/marketing/invites/settings" element={<Page><MarketingInviteSettings /></Page>} />
        <Route path="/marketing/invites/list" element={<Page><MarketingInviteList /></Page>} />
        <Route path="/marketing/invites/draws" element={<Page><MarketingInviteDraws /></Page>} />

        <Route path="/stores/list" element={<Page><StoresList /></Page>} />
        <Route path="/stores/list/:id" element={<Page><StoresDetail /></Page>} />
        <Route path="/stores/managers" element={<Page><StoreManagersList /></Page>} />
        {/* 保留旧路径通过 OLD_REDIRECTS 重定向，detail 直链也兼容 */}
        <Route path="/system/stores/:id" element={<Page><StoresDetail /></Page>} />
        <Route path="/system/staff" element={<Page><StaffList /></Page>} />
        <Route path="/system/admins" element={<Page><AdminsList /></Page>} />
        <Route path="/system/roles" element={<Page><RolesPage /></Page>} />
        <Route path="/system/audit-logs" element={<Page><AuditLogsList /></Page>} />
        <Route path="/system/about" element={<Page><SystemAbout /></Page>} />
        <Route path="/system/agreements" element={<Page><SystemAgreements /></Page>} />
        <Route path="/system/me" element={<Page><MyProfile /></Page>} />
      </Route>
    </Routes>
  );
}
